'use client';

/** The one message a pin drop leaves on the map: "Added Orbost" or "That
 *  spot's in the water", with a one-tap way out ("Nearest town is Marlo,
 *  12 km north" · "Add Marlo instead")
 *  and a way to try again ("Pick another spot"). Shown by the click with a
 *  caret pointing at it (or top-centre if there's no click point). It fades
 *  out on its own; see mapToastLifetime. */

import React from 'react';

export interface MapToastState {
  id: number;
  message: string;
  tone: 'success' | 'warning';
  /** Click point in map-container pixels, when the toast is about a click. */
  at?: { x: number; y: number };
  /** One-tap fix, e.g. snap a sea pin to the nearest town. */
  action?: { label: string; onClick: () => void; meta?: string };
  /** The quiet way out, e.g. "Pick another spot". */
  secondary?: { label: string; onClick: () => void };
  /** Quiet second line while something is still being worked out. */
  pending?: string;
  /** Quiet second line once it is ("Nearest town is Tamboon, 86 km north."). */
  detail?: string;
}

/** How long a toast stays: a bare refusal is read at a glance; one with an
 *  action needs time to reach for it. */
export function mapToastLifetime(t: Pick<MapToastState, 'tone' | 'action' | 'pending'>): number {
  if (t.action || t.pending) return 9000;
  return t.tone === 'warning' ? 2600 : 3200;
}

/** A 16×8 caret that joins the card's border, pointing at the click. */
function Caret({ dir, warn }: { dir: 'up' | 'down'; warn: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 8"
      width="16"
      height="8"
      style={{ left: 'calc(50% + var(--tail-x, 0px))' }}
      className={`absolute -translate-x-1/2 ${dir === 'up' ? '-top-[7px]' : '-bottom-[7px] rotate-180'}`}
    >
      <path d="M0 8 8 0l8 8" fill="#fff" />
      <path d="M0 8 8 0l8 8" fill="none" stroke={warn ? '#e3e8ef' : '#b7e4cf'} strokeWidth="1" />
    </svg>
  );
}

export default function MapToast({ toast, tail }: { toast: MapToastState; tail?: 'up' | 'down' }) {
  const warn = toast.tone === 'warning';
  const roomy = !!(toast.action || toast.secondary);
  return (
    <div
      role="status"
      data-testid="map-toast"
      className={`relative rounded-tp-lg border border-solid bg-white font-medium shadow-tp-pop ${
        roomy ? 'pointer-events-auto w-[336px] max-w-[calc(100vw-24px)] p-2' : 'max-w-[320px] py-2 pl-3 pr-3.5 text-tp-sm'
      } ${warn ? 'border-tp-line text-tp-ink' : 'border-tp-ok/25 text-tp-ok'}`}
    >
      {tail && <Caret dir={tail} warn={warn} />}
      <div className={`flex items-center gap-2.5 ${roomy ? 'px-1 pb-2.5 pt-1' : ''}`}>
        {warn ? (
          // Information, not an error: a helpful fix is one tap away.
          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-tp-info-soft text-tp-info">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" className="h-[18px] w-[18px]" aria-hidden="true">
              <path d="M2.5 12c1.5-1.3 3-1.3 4.5 0s3 1.3 4.5 0 3-1.3 4.5 0M2.5 8c1.5-1.3 3-1.3 4.5 0s3 1.3 4.5 0 3-1.3 4.5 0" />
            </svg>
          </span>
        ) : (
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 flex-none" aria-hidden="true">
            <path d="m5 10.5 3.2 3L15 6.5" />
          </svg>
        )}
        <span className="min-w-0">
          <span className={`block leading-snug ${roomy ? 'text-tp-lg font-semibold' : ''}`}>{toast.message}</span>
          {(toast.pending || toast.detail) && (
            <span className="block text-tp-sm font-normal leading-snug text-tp-muted">{toast.pending ?? toast.detail}</span>
          )}
        </span>
      </div>
      {toast.action && (
        <button
          type="button"
          onClick={toast.action.onClick}
          className="flex h-10 w-full min-h-0 min-w-0 items-center justify-center gap-1.5 rounded-tp border-0 bg-tp-brand px-3 text-tp-base font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-tp-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 focus-visible:ring-offset-1"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 flex-none" aria-hidden="true">
            <path d="m3.5 8.5 3 3 6-7" />
          </svg>
          <span className="truncate">{toast.action.label}</span>
          {toast.action.meta && <span className="flex-none font-medium text-white/80">· {toast.action.meta}</span>}
        </button>
      )}
      {toast.secondary && (
        <button
          type="button"
          onClick={toast.secondary.onClick}
          className="mt-1 flex h-9 w-full min-h-0 min-w-0 items-center justify-center rounded-tp border-0 bg-transparent px-3 text-tp-base font-medium text-tp-muted transition-colors duration-150 hover:bg-tp-surface hover:text-tp-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
        >
          {toast.secondary.label}
        </button>
      )}
    </div>
  );
}
